<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, defineEmits, reactive, ref } from 'vue';

import CardComponent from '@/components/ui/CardComponent.vue';
import { LegalDocumentTypeEnum } from '@/enums/legalDocument_enum.ts';

import type { CreateLegalDocumentRequest } from '@/types/services/legalDocument/legalDocument.type.ts';

const { t } = useI18n();
const emits = defineEmits(['closeModal']);

const formValid = ref<boolean>(false);

const documentTypes = computed(() => [
  { title: t('views.legalDocumentList.types.terms_of_service'), value: LegalDocumentTypeEnum.TERMS_OF_SERVICE },
  { title: t('views.legalDocumentList.types.privacy_policy'), value: LegalDocumentTypeEnum.PRIVACY_POLICY },
]);

const requiredRule = (value: unknown) => !!value || t('components.uploadLegalDocument.required');

const form = reactive<CreateLegalDocumentRequest>({
  documentType: null,
  file: null,
  changeNote: null,
});
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        {{ t('components.uploadLegalDocument.title') }}
      </template>
      <template #item>
        <p class="mb-4">{{ t('components.uploadLegalDocument.warning') }}</p>
        <v-form v-model="formValid" class="my-4">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="form.documentType"
                :items="documentTypes"
                :label="t('components.uploadLegalDocument.inputs.documentType')"
                :rules="[requiredRule]"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12">
              <v-file-input
                v-model="form.file"
                :label="t('components.uploadLegalDocument.inputs.file')"
                :rules="[requiredRule]"
                accept=".pdf"
                append-inner-icon="PhPaperclip"
                clearable
                hide-details="auto"
                prepend-icon=""
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.changeNote"
                :label="t('components.uploadLegalDocument.inputs.changeNote')"
                hide-details="auto"
                rows="3"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template #actions>
        <div class="d-flex flex-row justify-space-between ga-4 mt-2">
          <v-btn variant="outlined" @click.prevent="emits('closeModal', false)">
            {{ t('common.buttons.cancel') }}
          </v-btn>
          <v-btn
            :disabled="!formValid"
            color="primary"
            type="submit"
            variant="flat"
            @click="emits('closeModal', form)"
          >
            {{ t('common.buttons.validate') }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>
